import { Injectable } from '@angular/core';
import { ApiService } from '../api.service';
import { SyncCryptService } from './sync-crypt.service';
import { LoggerService } from '../logger.service';
import {
  ERROR_CODES,
  DirtyOutEncResultShare,
  DirtyOutEncResultSync,
  DirtyOutKeyItemDataKey,
  DirtyOutKeyItemShareKey,
  DirtyOutNeedEncDataKeys,
  DirtyOutNeedEncItem,
  DirtyOutTask,
  DirtyOutTaskDataKeyResult,
  DirtyOutTaskResult,
  DirtyOutTaskShareNameResult,
  ErrCode,
  FeatureFlags,
  KeysNeedEncByShareApiOutput,
  KeysNeedEncBySyncApiOutput,
  KeysSetEncApiOutput,
} from '../../shared/models';
import { UserService } from '../user.service';

@Injectable()
export class DirtyOutService {
  // Cache for share keys by sync_id to avoid redundant API calls
  // Files in the same folder share the same encryption keys
  private shareKeyCache = new Map<
    number,
    {
      data: DirtyOutKeyItemShareKey[];
      timestamp: number;
    }
  >();
  // Track in-flight requests to prevent duplicate concurrent API calls
  private pendingRequests = new Map<number, Promise<DirtyOutKeyItemShareKey[]>>();

  // Cache TTL in milliseconds (5 minutes)
  private readonly CACHE_TTL = 5 * 60 * 1000;

  constructor(
    private api: ApiService,
    private crypt: SyncCryptService,
    private log: LoggerService,
    private userService: UserService,
  ) {}

  public async processDirtyOut(workList: DirtyOutTask[]): Promise<void> {
    const processlist: DirtyOutTaskResult[] = [];
    for (let i = 0, len = workList.length; i < len; i++) {
      const item = workList[i];
      switch (item.action) {
        case 'encnameshare':
          processlist.push(await this.encShareName(item));
          break;
        case 'encdatakey':
          for (let j = 0, len2 = item.keys.length; j < len2; j++) {
            processlist.push(await this.encDataKey(item, item.keys[j]));
          }
          break;
        default:
          this.log.error('Processing dirty out, no action provided-' + item.toString());
          break;
      }
    }
    if (processlist.length) {
      await this.api.execute('keyssetdirtyout', {
        items: processlist,
      });
      return;
    } else {
      return;
    }
  }

  public async getKeyArrayForSyncId(
    sharedata: DirtyOutKeyItemShareKey[],
    syncId: number,
    plainName: string,
    dataKey?: string,
  ): Promise<DirtyOutEncResultSync[]> {
    const keysData: DirtyOutEncResultSync[] = [];
    for (let i = 0, len = sharedata.length; i < len; i++) {
      const shareitem = sharedata[i];
      let sharekey: string;
      try {
        sharekey = await this.crypt.sharekeyDecrypt(shareitem.enc_share_key, shareitem.sharekey_id);
      } catch (e) {
        const msg = [
          'Sharekey decrypt failed for ',
          syncId,
          ' key id = ',
          shareitem.sharekey_id,
          ' enc key = ',
          shareitem.enc_share_key,
        ].join('');
        this.log.e(msg, e);
        throw new ErrCode(ERROR_CODES.SHARE_KEY_MISSING, msg);
      }
      if (!sharekey) {
        throw new Error('No share key provided');
      }
      try {
        const encShareName = await this.crypt.filenameEncrypt(plainName, sharekey);
        const encDataKey = dataKey ? await this.crypt.datakeyEncrypt(dataKey, sharekey) : undefined;
        const keyData: DirtyOutEncResultSync = {
          enc_data_key: encDataKey,
          sync_id: syncId,
          sharekey_id: shareitem.sharekey_id,
          enc_share_name: encShareName,
          servtime: shareitem.servtime,
        };
        keysData.push(keyData);
      } catch (e) {
        const msg = [
          'Data key or file name failed ',
          syncId,
          ' key id = ',
          shareitem.sharekey_id,
          ' enc key = ',
          shareitem.enc_share_key,
        ].join('');
        this.log.e(msg, e);
        throw new ErrCode(ERROR_CODES.DATA_KEY_MISSING, msg);
      }
    }
    return keysData;
  }

  public async getShareKeyDataForSyncId(syncId: number): Promise<DirtyOutKeyItemShareKey[]> {
    if (!this.isConcurrentEnabled) {
      return await this.fetchShareKeyData(syncId);
    }

    // Check expired entries, cleanup the cache
    const now = Date.now();
    for (const [id, entry] of this.shareKeyCache) {
      if (now - entry.timestamp >= this.CACHE_TTL) {
        this.shareKeyCache.delete(id);
      }
    }

    const cached = this.shareKeyCache.get(syncId);
    if (cached) {
      this.log.info(`Using cached share keys for sync_id ${syncId}`);
      return cached.data;
    }

    // Check if request is already in flight (prevents duplicate concurrent API calls)
    if (this.pendingRequests.has(syncId)) {
      this.log.info(`Waiting for pending share key request for sync_id ${syncId}`);
      return this.pendingRequests.get(syncId);
    }

    // Make request and store promise for deduplication
    const requestPromise = this.fetchShareKeyData(syncId);
    this.pendingRequests.set(syncId, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } finally {
      this.pendingRequests.delete(syncId);
    }
  }

  private async fetchShareKeyData(syncId: number): Promise<DirtyOutKeyItemShareKey[]> {
    const result = await this.api.execute<KeysNeedEncBySyncApiOutput>('keysneedencbysync', {
      sync_id: syncId,
    });

    const sharedata: DirtyOutKeyItemShareKey[] = [];
    for (const [sharekeyId, encShareKey] of Object.entries(result.sharekeys)) {
      const item = new DirtyOutKeyItemShareKey();
      item.sharekey_id = sharekeyId;
      item.enc_share_key = encShareKey;
      item.servtime = result.servtime;
      sharedata.push(item);
    }

    if (this.isConcurrentEnabled) {
      // Store in cache
      this.shareKeyCache.set(syncId, {
        data: sharedata,
        timestamp: Date.now(),
      });
      this.log.info(`Cached share keys for sync_id ${syncId}`);
    }
    return sharedata;
  }

  public async executeForSync(syncId: number, plainName: string, dataKey?: string): Promise<DirtyOutEncResultSync[]> {
    try {
      const sharedata = await this.getShareKeyDataForSyncId(syncId);
      const result = await this.getKeyArrayForSyncId(sharedata, syncId, plainName, dataKey);
      return result;
    } catch (e) {
      const msg = ['executeForSync ', syncId].join('');
      this.log.e(msg, e);
      if (e instanceof ErrCode) {
        throw e;
      } else {
        throw new ErrCode(ERROR_CODES.DATA_KEY_MISSING);
      }
    }
  }

  public async executeForShare(shareId: number): Promise<KeysSetEncApiOutput> {
    const data = await this.api.execute<KeysNeedEncByShareApiOutput>('keysneedencbyshare', { share_id: shareId });
    const setItems: DirtyOutEncResultShare[] = [];
    const sharekeys = data.sharekeys;

    if (!data.items || !data.items.length) {
      this.log.warn('No items to execute for share id ' + shareId);
      return;
    }

    for (let i = 0, len = data.items.length; i < len; i++) {
      const cur = data.items[i];
      const sharekey = await this.crypt.sharekeyDecrypt(sharekeys[cur.new_key_id], cur.new_key_id);
      const filename = await this.crypt.filenameDecrypt(cur.metaname);
      const encShareName = await this.crypt.filenameEncrypt(filename, sharekey);
      const datakeys = await this.datakeyProcess(cur, cur.data_keys, sharekeys, sharekey);

      setItems.push({
        sync_id: cur.sync_id,
        share_id: cur.share_id,
        sharekey_id: cur.new_key_id,
        servtime: cur.servtime,
        enc_share_name: encShareName,
        data_keys: datakeys,
      });
    }
    return await this.api.execute<KeysSetEncApiOutput>('keyssetenc', {
      items: setItems,
    });
  }

  private async datakeyProcess(
    cur: DirtyOutNeedEncItem,
    datakeys: DirtyOutNeedEncDataKeys[],
    sharekeys: { [sharekey_id: string]: string },
    newShareKey: string,
  ): Promise<DirtyOutNeedEncDataKeys[]> {
    if (!datakeys || !datakeys.length) {
      this.log.info(cur.sync_id + ' has no datakeys to process');
      return [];
    }
    const newDataKeys: DirtyOutNeedEncDataKeys[] = [];
    for (let i = 0, len = datakeys.length; i < len; i++) {
      const curDk = datakeys[i];
      try {
        const plainShareKey = await this.crypt.sharekeyDecrypt(sharekeys[curDk.sharekey_id], curDk.sharekey_id);
        const dataKey = await this.crypt.datakeyDecrypt(curDk.enc_data_key, plainShareKey);
        const newEncDataKey = await this.crypt.datakeyEncrypt(dataKey, newShareKey);
        const result = new DirtyOutNeedEncDataKeys();
        result.sync_id = cur.sync_id;
        result.blob_id = cur.blob_id;
        result.enc_data_key = newEncDataKey;
        result.sharekey_id = curDk.sharekey_id;
        newDataKeys.push(result);
      } catch (ex) {
        // add logging but don't deal with the exception, re-throw
        this.log.error(`Error DirtyOut::datakeyProcess - datakey ${JSON.stringify(curDk)}`);
        this.log.error(`Error DirtyOut::datakeyProcess - current item: ${JSON.stringify(cur)}`);
        throw ex;
      }
    }
    return newDataKeys;
  }

  private async encDataKey(item: DirtyOutTask, key: DirtyOutKeyItemDataKey): Promise<DirtyOutTaskDataKeyResult> {
    const oldSharekeyId = `${key.old_share_id}-${key.old_share_sequence}`,
      sharekeyId = `${item.share_id}-${item.share_sequence}`;

    try {
      const newShareKey = await this.crypt.sharekeyDecrypt(key.new_share_key, sharekeyId);
      const shareKey = await this.crypt.sharekeyDecrypt(key.share_key, oldSharekeyId);
      const datakey = await this.crypt.datakeyDecrypt(key.data_key, shareKey);
      const encDataKey = await this.crypt.datakeyEncrypt(datakey, newShareKey);

      const result = new DirtyOutTaskDataKeyResult();
      result.action = 'encdatakey';
      result.blob_id = key.blob_id;
      result.sync_id = item.sync_id;
      result.share_id = item.share_id;
      result.share_sequence = item.share_sequence;
      result.enc_data_key = encDataKey;

      return result;
    } catch (ex) {
      // add logging but don't deal with the exception, re-throw
      this.log.error(`Error DirtyOut::encDataKey - sharekey_id ${sharekeyId} - old_sharekey_id: ${oldSharekeyId}`);
      this.log.error(`Error DirtyOut::encDataKey - Item: ${JSON.stringify(item)}`);
      this.log.error(`Error DirtyOut::encDataKey - key: ${JSON.stringify(key)}`);
      throw ex;
    }
  }

  private async encShareName(item: DirtyOutTask): Promise<DirtyOutTaskShareNameResult> {
    const shareKeyId = `${item.share_id}-${item.share_sequence}`;

    try {
      const filename = await this.crypt.filenameDecrypt(item.enc_share_name);
      const sharekey = await this.crypt.sharekeyDecrypt(item.share_key, shareKeyId);
      const encShareName = await this.crypt.filenameEncrypt(filename, sharekey);

      const result = new DirtyOutTaskShareNameResult();
      result.action = 'encnameshare';
      result.sync_id = item.sync_id;
      result.share_id = item.share_id;
      result.share_sequence = item.share_sequence;
      result.enc_share_name = encShareName;

      return result;
    } catch (ex) {
      // add logging but don't deal with the exception, re-throw
      this.log.error(`Error DirtyOut::encShareName - sharekey_id ${shareKeyId}`);
      this.log.error(`Error DirtyOut::encShareName - Item: ${JSON.stringify(item)}`);
      throw ex;
    }
  }

  private get isConcurrentEnabled(): boolean {
    return this.userService.hasFeatureFlag(FeatureFlags.CONCURRENT_UPLOAD);
  }
}
